import React, { useState, useEffect, useContext } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate, useLocation } from "react-router-dom"; // Importado useLocation

import { useUserAccountInfos } from "@hooks/useUserInfos";
import usePlanStatus from "../../hooks/usePlanStatus";
import Spinner from "../../components/UI/Spinner";
import defaultConfigDashboard from "./defaultConfigDashboard.json";
import SystemContext from "../../context/SystemContext";
import * as kRoutes from "../../constants/routes";

import { FirebaseContext } from "../../App";
import {
  getRemoteConfig,
  fetchAndActivate,
  getValue,
} from "firebase/remote-config";

import {
  PlanStatusCard,
  RecommendedGamesSection,
  NewReleaseCard,
  PersonalizedTrainingCard,
  GoalsAndRewardsCard,
  RecentMedalsSection,
  CoinStoreCard,
  UsersCard,
  DashboardHeader,
  OtherProductsCard,
  MyActivitiesCard,
} from "../../components/ModularDashboard";

const InformationArea = (props) => (
  <Grid
    container
    justifyContent={"space-between"}
    spacing={{ xs: 2, md: 4 }}
    flexGrow={1}
    height="100%"
  >
    {props.children}
  </Grid>
);

const mapComponents = {
  PlanStatusCard,
  RecommendedGamesSection,
  NewReleaseCard,
  PersonalizedTrainingCard,
  GoalsAndRewardsCard,
  RecentMedalsSection,
  CoinStoreCard,
  UsersCard,
  DashboardHeader,
  OtherProductsCard,
  InformationArea,
  MyActivitiesCard
};

const ModularDashboard = () => {
  const planInfo = usePlanStatus();
  const location = useLocation(); // Monitora alterações de URL via React Router

  const firebaseApp = useContext(FirebaseContext);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const [currentConfig, setCurrentConfig] = useState(null);

  const { loginInfo, changePropertyUrl } = useContext(SystemContext);
  const navigate = useNavigate();
  const currentUserQuery = useUserAccountInfos();
  const {
    data: currentUser,
    isLoading: currentUserIsLoading,
  } = currentUserQuery;


  // 1. Reage a alterações na URL e no perfil do usuário
  useEffect(() => {
    const profile = currentUser?.profile;
    const profileMap = { personal: "pessoal", professional: "profissional", patient: "paciente" };
    const profileSlug = profileMap[profile] ?? profile;
    
    if (profileSlug && !location.pathname.includes(profileSlug)) {
      changePropertyUrl(`/painel/${profileSlug}`);
    }
  }, [currentUser?.profile, location.pathname, changePropertyUrl]);

  useEffect(() => {
    if (loginInfo?.code) {
      navigate(kRoutes.R_RESCUE_CODE, { replace: true });
    }
  }, [loginInfo, navigate]);

  // 2. Reseta o config antigo e busca o novo ao alterar usuário ou perfil
  useEffect(() => {
    if (!firebaseApp || !currentUser?.typeProfile) {
      setFirebaseLoading(false);
      return;
    }


    setCurrentConfig(null); // Limpa layout anterior imediatamente
    setFirebaseLoading(true);

    const remoteConfig = getRemoteConfig(firebaseApp);
    remoteConfig.settings = {
      fetchTimeoutMillis: 60000,
      minimumFetchIntervalMillis: 60000,
    };

    fetchAndActivate(remoteConfig)
      .then(() => {
        const configsString = getValue(remoteConfig, currentUser?.typeProfile).asString();
        if (configsString) {
          setCurrentConfig(JSON.parse(configsString));
        } else if (defaultConfigDashboard.hasOwnProperty(currentUser?.typeProfile)) {
          setCurrentConfig(defaultConfigDashboard[currentUser?.typeProfile]);
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar configurações remotas:", error);
        if (defaultConfigDashboard.hasOwnProperty(currentUser?.typeProfile)) {
          setCurrentConfig(defaultConfigDashboard[currentUser?.typeProfile]);
        }
      })
      .finally(() => {
        setFirebaseLoading(false);
      });
  }, [firebaseApp, currentUser?.typeProfile]);



  const renderComponent = (componentConfig, index) => {
    const ComponentCustom = mapComponents[componentConfig["component"]];
    if (!ComponentCustom) return null;

    const spanSize = componentConfig["span"];
    const spanMdSize = componentConfig["spanMd"] ?? spanSize;
    const SubComponents = componentConfig["subComponent"]?.map((sub, i) => renderComponent(sub, i));

    // Prop key única garante que o componente seja destruído e recriado ao trocar de perfil/usuário
    const componentKey = `${currentUser?.sub}_${currentUser?.typeProfile}_${componentConfig["component"]}_${index}`;

    return (
      <Grid key={componentKey} item size={{ xs: spanSize, md: spanMdSize }}>
        <ComponentCustom
          currentUser={currentUser}
          currentUserQuery={currentUserQuery}
        >
          {SubComponents}
        </ComponentCustom>
      </Grid>
    );
  };

  const isWaitingForCurrentUser = !currentUser && currentUserIsLoading;

  if (isWaitingForCurrentUser || planInfo.isLoading || firebaseLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
        <Spinner center />
      </Box>
    );
  }

  return (
    <Grid 
      container 
      key={`${currentUser?.sub}_${currentUser?.typeProfile}`} // Key no contêiner principal força o remount completo
      sx={{ mx: { xs: 2, md: 10 }, marginBottom: 4 }} 
      id="modular-dashboard"
    >
      <DashboardHeader
        currentUser={currentUser}
        currentUserQuery={currentUserQuery}
      />
      {currentConfig?.schema && (
        <Grid container item spacing={{ xs: 2, md: 4 }} flexGrow={1}>
          {currentConfig.schema.map((config, index) => renderComponent(config, index))}
        </Grid>
      )}
    </Grid>
  );
};

export default ModularDashboard;
